import {
  readInitialCollectionIds,
  sameCollectionIds,
} from '../../../../src/apps/tracer/collection-inventory.mjs';

type Reply = { id?: unknown; result?: { initialCollectionIds?: unknown }; error?: unknown };
type Pending = {
  id: string;
  resolve(ids: readonly string[]): void;
  reject(error: Error): void;
  timer: ReturnType<typeof setTimeout>;
};

/** One current preview port. This captures declarations, never authentication. */
export function createPreviewCollectionInventory(timeoutMs = 20_000) {
  let port: MessagePort | null = null;
  let initial: readonly string[] | null = null;
  let pending: Pending | null = null;
  function finish() {
    const request = pending;
    pending = null;
    if (request) clearTimeout(request.timer);
    return request;
  }
  function invalidate() {
    port = null;
    initial = null;
    finish()?.reject(Error('collection_inventory_unavailable'));
  }
  function admit(value: unknown) {
    if (!port) throw Error('collection_inventory_unavailable');
    const ids = readInitialCollectionIds(value);
    if (initial && !sameCollectionIds(initial, ids)) throw Error('collection_inventory_changed');
    initial = ids;
    return ids;
  }
  return {
    bind(current: MessagePort) {
      invalidate();
      port = current;
      return () => {
        if (port === current) invalidate();
      };
    },
    admit,
    receive(value: Reply) {
      if (!pending || value?.id !== pending.id) return false;
      const request = finish()!;
      try {
        if (value.error) throw Error('collection_inventory_unavailable');
        request.resolve(admit(value.result?.initialCollectionIds));
      } catch (error) {
        request.reject(error instanceof Error ? error : Error('collection_inventory_invalid'));
      }
      return true;
    },
    read(): Promise<readonly string[]> {
      if (!port || pending) return Promise.reject(Error('collection_inventory_unavailable'));
      return new Promise((resolve, reject) => {
        const id = crypto.randomUUID();
        const timer = setTimeout(
          () => finish()?.reject(Error('collection_inventory_unavailable')),
          timeoutMs,
        );
        pending = { id, resolve, reject, timer };
        try {
          port!.postMessage({ id, operation: 'collections.inventory', input: {} });
        } catch {
          finish()?.reject(Error('collection_inventory_unavailable'));
        }
      });
    },
  };
}
export type PreviewCollectionInventory = ReturnType<typeof createPreviewCollectionInventory>;
